import { InjectionToken } from "@angular/core";
import {
  ALL_FORMATS,
  BlobSource,
  BufferTarget,
  CanvasSink,
  Conversion,
  Input,
  Mp4OutputFormat,
  Output,
  Quality,
} from "mediabunny";

/**
 * Imported videos are converted to match what the mobile app records with the
 * Celo camera: at most 30 seconds, 720p, 30fps and 1 Mbps, saved as mp4.
 *
 * See the "Video - Mobile spec" page in the Celo Product WIKI.
 */
export const MAX_VIDEO_DURATION_SECONDS = 30;
const MAX_VIDEO_LONG_SIDE = 1280;
const MAX_VIDEO_SHORT_SIDE = 720;
const VIDEO_FRAME_RATE = 30;
const VIDEO_BITRATE = 1_000_000;

// The encoder treats the bitrate as a target, so very grainy video can come out
// larger. The API's default request size limit is about 28.6 MB.
export const MAX_VIDEO_FILE_BYTES = 25 * 1024 * 1024;

// Recorded 30 second clips often measure slightly over 30 seconds.
const DURATION_TOLERANCE_SECONDS = 0.5;

export const SUPPORTED_VIDEO_FORMATS = ["video/mp4", "video/quicktime"];

export class VideoTooLongError extends Error {
  public constructor() {
    super(
      `Videos must be ${MAX_VIDEO_DURATION_SECONDS} seconds or shorter.`
    );
    this.name = "VideoTooLongError";
  }
}

export class VideoTooLargeError extends Error {
  public constructor() {
    super("This video is too large to upload, even after compressing it.");
    this.name = "VideoTooLargeError";
  }
}

export interface PreparedVideo {
  /** The converted mp4 video. */
  file: File;
  /** A JPEG of the first frame. The API requires one for every video. */
  thumbnail: File;
}

export interface PrepareVideoOptions {
  /** Reject the converted video if it is larger than this. */
  maxFileBytes?: number;
  /** Called with the thumbnail as soon as it exists, before the conversion. */
  onThumbnail?: (thumbnail: File) => void;
}

/**
 * The function used to convert imported videos. Tests replace it, since the
 * unit test environment has no video encoder.
 */
export const VIDEO_PREPARER = new InjectionToken<
  (file: File, options?: PrepareVideoOptions) => Promise<PreparedVideo>
>("VIDEO_PREPARER", {
  providedIn: "root",
  factory: () => prepareVideoForUpload,
});

export function isSupportedVideo(file: { type?: string | null }): boolean {
  return SUPPORTED_VIDEO_FORMATS.includes(file.type ?? "");
}

/**
 * Converts a video file to the recording spec and creates its thumbnail.
 *
 * Throws `VideoTooLongError` when the video is longer than 30 seconds, and
 * `VideoTooLargeError` when the converted file is over `maxFileBytes`.
 */
export async function prepareVideoForUpload(
  file: File,
  options: PrepareVideoOptions = {}
): Promise<PreparedVideo> {
  const maxFileBytes = options.maxFileBytes ?? MAX_VIDEO_FILE_BYTES;
  const input = new Input({
    formats: ALL_FORMATS,
    source: new BlobSource(file),
  });

  const videoTrack = await input.getPrimaryVideoTrack();
  if (!videoTrack) {
    throw new Error("The file does not contain a video track.");
  }

  const duration = await input.computeDuration();
  if (duration > MAX_VIDEO_DURATION_SECONDS + DURATION_TOLERANCE_SECONDS) {
    throw new VideoTooLongError();
  }

  const { width, height } = fitWithin720p(
    videoTrack.displayWidth,
    videoTrack.displayHeight
  );

  // The thumbnail takes milliseconds, the conversion takes seconds, so the
  // caller can show the thumbnail while the conversion runs.
  const thumbnail = await createThumbnail(file, videoTrack, width, height);
  options.onThumbnail?.(thumbnail);

  const output = new Output({
    format: new Mp4OutputFormat({ fastStart: "in-memory" }),
    target: new BufferTarget(),
  });

  const conversion = await Conversion.init({
    input,
    output,
    video: {
      codec: "avc",
      width,
      height,
      fit: "contain",
      frameRate: VIDEO_FRAME_RATE,
      quality: new Quality({ bitrate: VIDEO_BITRATE }),
      forceTranscode: true,
    },
    audio: {
      codec: "aac",
    },
  });

  if (conversion.discardedTracks.some((t) => t.track.type === "video")) {
    throw new Error("The video could not be converted on this device.");
  }

  await conversion.execute();

  if (output.target.buffer.byteLength > maxFileBytes) {
    throw new VideoTooLargeError();
  }

  const videoFile = new File(
    [output.target.buffer],
    replaceExtension(file.name, "mp4"),
    { type: "video/mp4" }
  );

  return { file: videoFile, thumbnail };
}

/**
 * Scales dimensions down to fit 1280x720 (or 720x1280 for portrait video),
 * keeping the aspect ratio. Never scales up. The H.264 encoder needs even
 * dimensions.
 */
export function fitWithin720p(
  width: number,
  height: number
): { width: number; height: number } {
  const isLandscape = width >= height;
  const maxWidth = isLandscape ? MAX_VIDEO_LONG_SIDE : MAX_VIDEO_SHORT_SIDE;
  const maxHeight = isLandscape ? MAX_VIDEO_SHORT_SIDE : MAX_VIDEO_LONG_SIDE;
  const scale = Math.min(1, maxWidth / width, maxHeight / height);

  return {
    width: toEven(width * scale),
    height: toEven(height * scale),
  };
}

async function createThumbnail(
  file: File,
  videoTrack: Awaited<ReturnType<Input["getPrimaryVideoTrack"]>>,
  width: number,
  height: number
): Promise<File> {
  const sink = new CanvasSink(videoTrack, { width, height, fit: "contain" });
  const firstTimestamp = await videoTrack.getFirstTimestamp();
  const frame = await sink.getCanvas(firstTimestamp);
  if (!frame) {
    throw new Error("Could not read the first frame of the video.");
  }

  const blob = await canvasToJpeg(frame.canvas);
  return new File([blob], replaceExtension(file.name, "jpg"), {
    type: "image/jpeg",
  });
}

function canvasToJpeg(
  canvas: HTMLCanvasElement | OffscreenCanvas
): Promise<Blob> {
  if (canvas instanceof OffscreenCanvas) {
    return canvas.convertToBlob({ type: "image/jpeg", quality: 0.85 });
  }
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) =>
        blob ? resolve(blob) : reject(new Error("Could not create thumbnail.")),
      "image/jpeg",
      0.85
    );
  });
}

function toEven(value: number): number {
  return Math.max(2, Math.round(value / 2) * 2);
}

function replaceExtension(fileName: string, extension: string): string {
  const dot = fileName.lastIndexOf(".");
  const baseName = dot > 0 ? fileName.substring(0, dot) : fileName;
  return `${baseName}.${extension}`;
}
